import Papa from "papaparse";
import { Fragment, useRef, useState } from "react";
import { LoadingBar } from "@src/components/Utility/LoadingBar";
import { parseCSVDataIntoIntervalsDoordash } from "@src/utility_functions/parseFunctions/parseCSVDataIntoIntervalsDoordash";
import { parseJSONDataIntoIntervals } from "@src/utility_functions/parseFunctions/parseJSONDataIntoIntervals";
import { parseCSVDataIntoIntervalsNetflix } from "@src/utility_functions/parseFunctions/parseCSVDataIntoIntervalsNetflix";
import { parseCSVDataIntoIntervalsLinkedIn } from "@src/utility_functions/parseFunctions/parseCSVDataIntoIntervalsLinkedIn";
import type { InputValidationError } from "@src/types/timeline.ts";

export const FileUploadInput = (props) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileError, setFileError] = useState<InputValidationError>({
    errorClass: "",
    errorMessages: [],
  });
  const [fileDropped, setFileDropped] = useState(false);
  const [parsedItemCount, setParsedItemCount] = useState(0);
  const [itemsTotal, setItemsTotal] = useState(0);

  const allowedFileTypesList = {
    custom: ["text/csv", "application/json"],
    doordash: ["text/csv"],
    netflix: ["text/csv"],
    linkedin: ["text/csv"],
  };
  const allowedFileTypes = allowedFileTypesList[props.type];
  const acceptAttr = allowedFileTypes.join(",");

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.fileUploadDisabled) {
      return;
    }
    const file = event.dataTransfer.items[0];
    const fileType = file.type;
    if (allowedFileTypes.includes(fileType)) {
      setFileError({
        errorClass: "valid",
        errorMessages: [],
      });
    } else {
      setFileError({
        errorClass: "invalid",
        errorMessages: ["File type must be CSV or JSON."],
      });
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.fileUploadDisabled) {
      return;
    }
    setFileError({
      errorClass: "",
      errorMessages: [],
    });
  };
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.fileUploadDisabled) {
      return;
    }
  };

  const processFile = async (file: File) => {
    if (props.fileUploadDisabled) {
      return;
    }
    setFileError({
      errorClass: "",
      errorMessages: [],
    });
    setFileDropped(true);
    setParsedItemCount(0);
    setItemsTotal(0);
    props.setIsLoading(true);

    const fileSize = file.size;
    const fileType = file.type;
    const fileSizeLimit = props.fileSizeLimitMBs * 1024 * 1024;
    const condition1 = allowedFileTypes.includes(fileType);
    const condition2 = fileSize < fileSizeLimit;
    if (!condition1 || !condition2) {
      if (!condition1) {
        setFileError({
          errorClass: "invalid",
          errorMessages: ["File type must be CSV or JSON."],
        });
      }
      if (!condition2) {
        setFileError({
          errorClass: "invalid",
          errorMessages: ["File size must be less than 10MB."],
        });
      }
      props.setIsLoading(false);
      return;
    }

    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = async (ev) => {
      const fileContent = ev.target?.result ?? "";
      props.setFileName(file.name);
      let parsedData: any[] = [];
      let timelineDataObject: any;

      const fileType = file.type;
      if (fileType === "application/json") {
        const jsonString = typeof fileContent === "string" ? fileContent : "";
        parsedData = JSON.parse(jsonString);
      }
      if (fileType === "text/csv") {
        const parsedDataRaw = Papa.parse(fileContent as string, {
          header: true,
        });
        parsedData = parsedDataRaw.data as any[];
      }
      setItemsTotal(parsedData.length || 0);
      if (props.type === "doordash") {
        timelineDataObject = await parseCSVDataIntoIntervalsDoordash(
          parsedData,
          setParsedItemCount,
          setFileError,
        );
      } else if (props.type === "netflix") {
        timelineDataObject = await parseCSVDataIntoIntervalsNetflix(
          parsedData,
          setParsedItemCount,
          setFileError,
        );
      } else if (props.type === "linkedin") {
        timelineDataObject = await parseCSVDataIntoIntervalsLinkedIn(
          parsedData,
          setParsedItemCount,
          setFileError,
        );
      } else if (props.type === "custom") {
        timelineDataObject = await parseJSONDataIntoIntervals(
          parsedData,
          setParsedItemCount,
          setFileError,
        );
      }
      if (timelineDataObject.errors > 100) {
        // Exceeded error threshold; do not enable submit
      } else {
        props.setProcessedData(timelineDataObject);
        props.setSubmitButtonDisabled(false);
      }
      props.setIsLoading(false);
      if (props.setTextAreaDisabled) {
        props.setTextAreaDisabled(true);
      }
    };
    reader.onerror = (_error) => {
      props.setIsLoading(false);
    };
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.fileUploadDisabled) {
      return;
    }
    const file = event.dataTransfer.files[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleFileInputChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
      event.target.value = "";
    }
  };

  const openFileDialog = () => {
    if (props.fileUploadDisabled) return;
    fileInputRef.current?.click();
  };

  const onKeyDown = (e) => {
    if (props.fileUploadDisabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openFileDialog();
    }
  };
  return (
    <>
      <div
        className={
          "fileUploadInputDiv" +
          (fileError?.errorClass === "valid"
            ? " fileUploadInputDiv_draggedOverValid"
            : "") +
          (fileError?.errorClass === "invalid"
            ? " fileUploadInputDiv_draggedOverInvalid"
            : "") +
          (props.fileUploadDisabled ? " disabled" : "")
        }
        role="button"
        tabIndex={props.fileUploadDisabled ? -1 : 0}
        aria-disabled={props.fileUploadDisabled || undefined}
        onClick={openFileDialog}
        onKeyDown={onKeyDown}
        onDrop={(event) => handleDrop(event)}
        onDragOver={(event) => handleDragOver(event)}
        onDragEnter={(event) => handleDragEnter(event)}
        onDragLeave={(event) => handleDragLeave(event)}
      >
        <div className="fileUploadInputDiv_textContainer _pointer-events--none ">
          <i className="fa-regular fa-file _color--var_gray-40_"></i>
          <div>
            <p>Drag and drop your file here</p>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptAttr}
          style={{
            position: "absolute",
            opacity: 0,
            width: 0.1,
            height: 0.1,
            pointerEvents: "none",
          }}
          onChange={handleFileInputChange}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      {fileError?.errorClass === "invalid" && (
        <div className="field__message field__message--invalid">
          {fileError.errorMessages.map((str, index) => {
            return (
              <Fragment key={index}>
                {str}
                <br />
              </Fragment>
            );
          })}
        </div>
      )}
      {fileDropped && (
        <>
          <LoadingBar
            text={props.fileName}
            statusNumber={parsedItemCount}
            totalNumber={itemsTotal}
          />
          <ClearFileIcon
            setFileDropped={setFileDropped}
            setFileError={setFileError}
            setFileName={props.setFileName}
            setProcessedData={props.setProcessedData}
            setTextAreaDisabled={props.setTextAreaDisabled || false}
            setSubmitButtonDisabled={props.setSubmitButtonDisabled}
            setFileUploadDisabled={props.setFileUploadDisabled}
            setIsLoading={props.setIsLoading}
          />
        </>
      )}
    </>
  );
};

export const ClearFileIcon = (props) => {
  return (
    <div
      className="_text-align--right"
      onClick={() => {
        props.setFileDropped(false);
        props.setFileError({
          errorClass: "",
          errorMessages: [],
        });
        props.setFileName("");
        props.setProcessedData(null);
        if (props.setTextAreaDisabled) {
          props.setTextAreaDisabled(false);
        }
        props.setSubmitButtonDisabled(true);
        props.setFileUploadDisabled(false);
        props.setIsLoading(false);
      }}
    >
      {/*
      <i className="fa-solid fa-file-xmark _font-size--2rem _color--var_gray-40_" title="Clear File"></i>
      */}
    </div>
  );
};
